<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Left Sidebar Navigation -->
    <MemberPortalNavigation />

    <!-- Main Content -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18"></h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/member-dashboard">Dashboard</router-link></li>
                    <li class="breadcrumb-item active"></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading your membership card...</p>
          </div>

          <!-- Main Content -->
          <div v-else>
            <!-- Card Not Issued Alert -->
            <div class="row" v-if="!isCardIssued">
              <div class="col-12">
                <div class="alert alert-warning mb-4">
                  <div class="d-flex align-items-center">
                    <i class="bx bx-info-circle fs-2 me-3"></i>
                    <div>
                      <h6 class="alert-heading mb-1">Card Not Yet Issued</h6>
                      <p class="mb-0">
                        Your CBHI membership card has not been issued yet. Please visit your local CBHI office to collect your card.
                        You can still download a digital copy below.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card Issued Success -->
            <div class="row" v-else>
              <div class="col-12">
                <div class="alert alert-success mb-4">
                  <div class="d-flex align-items-center">
                    <i class="bx bx-check-circle fs-2 me-3"></i>
                    <div>
                      <h6 class="alert-heading mb-1">Card Issued</h6>
                      <p class="mb-0">
                        Your membership card was issued on <strong>{{ formatDate(cardIssueDate) }}</strong>.
                        Present this card at any contracted health facility to access services.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Member Info Summary -->
            <div class="row mb-4">
              <div class="col-xl-6 col-lg-6">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-user text-primary me-2"></i>Member Information
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="info-list">
                      <div class="info-item">
                        <span class="info-label">Full Name</span>
                        <span class="info-value">{{ memberFullName }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">CBHI Number</span>
                        <span class="info-value text-primary fw-bold">{{ memberInfo.cbhi_number }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Plan</span>
                        <span class="info-value">
                          <span class="badge bg-primary">{{ planInfo.plan_name }}</span>
                        </span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Coverage Limit</span>
                        <span class="info-value">{{ formatCurrency(planInfo.annual_coverage_limit) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-6 col-lg-6 mt-3 mt-lg-0">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-shield-check text-primary me-2"></i>Coverage Details
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="info-list">
                      <div class="info-item">
                        <span class="info-label">Total Members Covered</span>
                        <span class="info-value">{{ totalMembers }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Dependents</span>
                        <span class="info-value">{{ beneficiaryCount }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Valid Until</span>
                        <span class="info-value" :class="isExpiringSoon ? 'text-danger fw-bold' : ''">
                          {{ formatDate(memberInfo.expiry_date) }}
                          <span v-if="isExpiringSoon" class="badge bg-danger ms-2">Expiring Soon</span>
                        </span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Status</span>
                        <span class="info-value">
                          <span class="badge" :class="getStatusBadgeClass()">
                            {{ capitalizeFirst(memberInfo.status) }}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- CBHI Card Component -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-id-card text-primary me-2"></i>Your CBHI Membership Card
                    </h5>
                  </div>
                  <div class="card-body">
                    <p class="text-muted mb-4">
                      This is your digital CBHI membership card. You can flip it to see the back, print it, or download it as a PDF.
                    </p>

                    <!-- Reusing CBHICard Component -->

                    <CBHICard
                        ref="cbhiCard"
                        :member-info="cardMemberInfo"
                        :plan-info="planInfo"
                        :beneficiary-count="beneficiaryCount"
                        :issue-date="cardIssueDate || today"
                        :print-mode="false"
                    />

                  </div>
                </div>
              </div>
            </div>

            <!-- Usage Tips -->
            <div class="row mt-4">
              <div class="col-12">
                <div class="card bg-light border-0">
                  <div class="card-body">
                    <h6 class="mb-3"><i class="bx bx-bulb text-warning me-2"></i>How to Use Your Card</h6>
                    <div class="row">
                      <div class="col-md-4 mb-3 mb-md-0">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="tip-number">1</div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1">Present at Facility</h6>
                            <p class="text-muted small mb-0">Show this card at any contracted health facility when seeking services.</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4 mb-3 mb-md-0">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="tip-number">2</div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1">Verify Identity</h6>
                            <p class="text-muted small mb-0">Bring a valid ID along with your CBHI card for verification.</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0">
                            <div class="tip-number">3</div>
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h6 class="mb-1">Keep it Safe</h6>
                            <p class="text-muted small mb-0">Report immediately if lost. Download a backup copy for emergencies.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Links -->
            <div class="row mt-4">
              <div class="col-12">
                <div class="d-flex flex-wrap gap-2 justify-content-center">
                  <router-link to="/find-facilities" class="btn btn-outline-primary">
                    <i class="bx bx-hospital me-1"></i>Find Health Facility
                  </router-link>
                  <router-link to="/member-coverage" class="btn btn-outline-info">
                    <i class="bx bx-shield-check me-1"></i>View Coverage Details
                  </router-link>
                  <router-link to="/beneficiaries-list" class="btn btn-outline-success">
                    <i class="bx bx-group me-1"></i>View Beneficiaries
                  </router-link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import MemberPortalNavigation from "@/components/member-portal/MemberPortalNavigation.vue";
import CBHICard from "@/components/cbhi-member-management/CBHICard.vue";
import { memberUrl, executeJson } from "@/api";

export default {
  name: "MyMembershipCard",
  components: {
    MemberPortalNavigation,
    CBHICard
  },

  data() {
    return {
      loading: false,

      // Profile data
      profile: {},
      planInfo: {},
      beneficiaryCount: 0,

      today: new Date().toISOString().split('T')[0]
    };
  },

  computed: {
    memberInfo() {
      return this.profile;
    },

    // Format member info for the CBHICard component (supports both naming conventions)
    cardMemberInfo() {
      return {
        // Snake case (what API returns)
        cbhi_number: this.profile.cbhi_number,
        full_name: this.profile.full_name,
        date_of_birth: this.profile.date_of_birth,
        gender: this.profile.gender,
        profile_photo: this.profile.profile_photo,
        expiry_date: this.profile.expiry_date,
        region_name: this.profile.region_name,
        woreda_name: this.profile.woreda_name,
        status: this.profile.status,

        // Camel case (for CBHICard component compatibility)
        cbhiNumber: this.profile.cbhi_number,
        fullName: this.profile.full_name,
        dateOfBirth: this.profile.date_of_birth,
        profilePhoto: this.profile.profile_photo,
        expiryDate: this.profile.expiry_date,
        regionName: this.profile.region_name,
        woredaName: this.profile.woreda_name
      };
    },

    memberFullName() {
      return this.profile.full_name || 'Member';
    },

    isCardIssued() {
      return this.profile.cbhi_card_issued === 1 ||
          this.profile.cbhi_card_issued === '1';
    },

    cardIssueDate() {
      return this.profile.card_issue_date;
    },

    totalMembers() {
      return 1 + parseInt(this.beneficiaryCount || 0);
    },

    // Check if expiring within 30 days
    isExpiringSoon() {
      if (!this.profile.expiry_date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiry = new Date(this.profile.expiry_date);
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 30 && diffDays > 0;
    }
  },

  mounted() {
    this.loadMemberData();
  },

  methods: {
    async loadMemberData() {
      this.loading = true;

      const data = {
        function: "getMyMemberProfile"
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.profile = res.data.data.profile || {};
          this.planInfo = res.data.data.planInfo || {};
          this.beneficiaryCount = parseInt(res.data.data.beneficiaryCount) || 0;
        }
      } catch (error) {
        console.error('Error loading member data:', error);
      } finally {
        this.loading = false;
      }
    },

    getStatusBadgeClass() {
      const statusMap = {
        'active': 'bg-success',
        'inactive': 'bg-warning',
        'suspended': 'bg-danger',
        'expired': 'bg-secondary'
      };
      return statusMap[this.profile.status] || 'bg-secondary';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat().format(amount);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }
};
</script>

<style scoped>
/* Content area with sidebar */
.content-minimize {
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.page-content {
  padding: 70px 0 60px 0;
  min-height: 100vh;
  background-color: #f8f9fa;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0 !important;
  }
}

.vertical-collpsed .content-minimize {
  margin-left: 70px;
}

/* Cards */
.card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
  background: transparent;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.25rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3f70;
  margin: 0;
}

/* Info List */
.info-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.info-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.info-value {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
}

/* Alert Styling */
.alert {
  border-radius: 0.75rem;
  border: none;
}

.alert-warning {
  background-color: rgba(255, 193, 7, 0.15);
  color: #856404;
}

.alert-success {
  background-color: rgba(40, 167, 69, 0.15);
  color: #155724;
}

/* Tip Numbers */
.tip-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 768px) {
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .tip-number {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }
}
</style>